import { WA_PHYSICS, type Vector2 } from '../core/constants';
import type { Worm } from '../entities/worm';
import type { Projectile } from '../entities/projectile';

export interface WormHitResult {
  worm: Worm;
  hitPoint: Vector2;
}

export interface WormCollisionResult {
  wormA: Worm;
  wormB: Worm;
  pushDirection: Vector2;
  overlapDepth: number;
}

export function getWormHitbox(worm: Worm): { x: number; y: number; width: number; height: number } {
  return {
    x: worm.position.x,
    y: worm.position.y,
    width: WA_PHYSICS.WORM.WIDTH,
    height: WA_PHYSICS.WORM.HEIGHT,
  };
}

export function pointInWormHitbox(px: number, py: number, worm: Worm): boolean {
  const box = getWormHitbox(worm);
  return px >= box.x && px <= box.x + box.width && py >= box.y && py <= box.y + box.height;
}

export function checkProjectileWormHit(
  projectile: Projectile,
  worms: Worm[],
  excludeOwnerId = true,
): WormHitResult | null {
  for (const worm of worms) {
    if (worm.state === 'dead') continue;
    if (excludeOwnerId && worm.id === projectile.ownerId) continue;

    if (pointInWormHitbox(projectile.position.x, projectile.position.y, worm)) {
      return {
        worm,
        hitPoint: { x: projectile.position.x, y: projectile.position.y },
      };
    }
  }
  return null;
}

export function checkWormWormCollision(worm: Worm, otherWorms: Worm[]): WormCollisionResult | null {
  if (worm.state === 'dead') return null;

  const wormSpeed = Math.sqrt(worm.velocity.x ** 2 + worm.velocity.y ** 2);
  if (wormSpeed < WA_PHYSICS.COLLISION.WORM_COLLISION_VELOCITY_THRESHOLD) return null;

  const boxA = getWormHitbox(worm);

  for (const other of otherWorms) {
    if (other.id === worm.id) continue;
    if (other.state === 'dead') continue;

    const boxB = getWormHitbox(other);

    const overlapX = Math.min(boxA.x + boxA.width, boxB.x + boxB.width) - Math.max(boxA.x, boxB.x);
    const overlapY =
      Math.min(boxA.y + boxA.height, boxB.y + boxB.height) - Math.max(boxA.y, boxB.y);

    if (overlapX > 0 && overlapY > 0) {
      const centerA = { x: boxA.x + boxA.width / 2, y: boxA.y + boxA.height / 2 };
      const centerB = { x: boxB.x + boxB.width / 2, y: boxB.y + boxB.height / 2 };

      const isFromAbove = centerA.y < centerB.y && worm.velocity.y > 0;
      const horizontalSpeed = Math.abs(worm.velocity.x);

      if (isFromAbove && horizontalSpeed < 30) {
        worm.position.y = boxB.y - WA_PHYSICS.WORM.HEIGHT;
        worm.velocity.y = 0;

        if (horizontalSpeed < 5) {
          const slideDir = centerA.x < centerB.x ? -1 : 1;
          worm.velocity.x = slideDir * 30;
        }
        continue;
      }

      let dx = centerB.x - centerA.x;
      let dy = centerB.y - centerA.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 0) {
        dx /= dist;
        dy /= dist;
      } else {
        dx = 1;
        dy = 0;
      }

      return {
        wormA: worm,
        wormB: other,
        pushDirection: { x: dx, y: dy },
        overlapDepth: Math.min(overlapX, overlapY),
      };
    }
  }

  return null;
}

export function checkProjectileWormBodyCollision(
  projectile: Projectile,
  worms: Worm[],
): { worm: Worm; normal: Vector2 } | null {
  for (const worm of worms) {
    if (worm.state === 'dead') continue;

    const box = getWormHitbox(worm);
    const px = projectile.position.x;
    const py = projectile.position.y;

    if (px >= box.x && px <= box.x + box.width && py >= box.y && py <= box.y + box.height) {
      const centerX = box.x + box.width / 2;
      const centerY = box.y + box.height / 2;

      let nx = px - centerX;
      let ny = py - centerY;
      const len = Math.sqrt(nx * nx + ny * ny);

      if (len > 0) {
        nx /= len;
        ny /= len;
      } else {
        nx = 0;
        ny = -1;
      }

      return { worm, normal: { x: nx, y: ny } };
    }
  }
  return null;
}

export function resolveWormCollision(collision: WormCollisionResult): void {
  const { wormA, wormB, pushDirection, overlapDepth } = collision;

  const speedA = Math.sqrt(wormA.velocity.x ** 2 + wormA.velocity.y ** 2);
  const speedB = Math.sqrt(wormB.velocity.x ** 2 + wormB.velocity.y ** 2);

  const pushForce = WA_PHYSICS.COLLISION.WORM_WORM_PUSH_FORCE;
  const momentumRatio = speedA / (speedA + speedB + 1);

  const separationX = pushDirection.x * (overlapDepth / 2 + 1);
  const separationY = pushDirection.y * (overlapDepth / 2 + 1);

  wormA.position.x -= separationX;
  wormA.position.y -= separationY;
  wormB.position.x += separationX;
  wormB.position.y += separationY;

  const pushToB = pushForce * momentumRatio;
  wormB.velocity.x += pushDirection.x * pushToB;
  wormB.velocity.y += pushDirection.y * pushToB - pushForce * 0.3;
  wormB.isThrown = true;
  wormB.isGrounded = false;

  wormA.velocity.x *= 0.5;
  wormA.velocity.y *= 0.5;
}
